import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CONNECTIONS_PATH } from '../services/GitService';
import { RUN_DONE, getDeployFiles, getImageRun, publishBranchName } from '../services/PublishTargetsService';

/*
 * The Publish dialog's destinations beyond Console (vfs-service's /publish/git, /publish/gitops and the gateway's
 * /publish/images): the confirm steps, the image build's progress, the results, and the deployment files' preview.
 * PublishDialog.jsx owns the steps; these render one step each.
 */

export const RUN_POLL_MS = 2000;
export const DEFAULT_ENVIRONMENTS = ['dev', 'prod'];
const PROVIDER_NAMES = { github: 'GitHub', gitlab: 'GitLab', 'azure-devops': 'Azure DevOps', bitbucket: 'Bitbucket' };
export const providerName = (type) => PROVIDER_NAMES[type] || type || 'Git';
const shortDigest = (digest) => (typeof digest === 'string' ? digest.replace(/^sha256:/, '').slice(0, 12) : '');
const hostOf = (url) => { try { return new URL(url).host; } catch { return ''; } };

/** `acme/fleet-tracker` from the repository's web address (the address itself when it is not one). */
export function repoPath(url) {
  try { return new URL(url).pathname.replace(/^\/+|\/+$/g, '').replace(/\.git$/, ''); } catch { return url || ''; }
}

/** An image tag from the deploy version: `0.1.0+ab12cd` → `0.1.0-ab12cd` (a tag has no `+`). */
export const tagFromVersion = (version) => String(version || '').replace(/\+/g, '-').replace(/[^A-Za-z0-9_.-]/g, '').slice(0, 128);

/** `helm install fleet-tracker oci://ghcr.io/acme/charts/fleet-tracker --version 0.1.0`. */
export function helmInstall(chart) {
  if (!chart?.ref) { return ''; }
  const name = chart.ref.replace(/\/+$/, '').split('/').pop();
  return `helm install ${name} ${chart.ref}${chart.version ? ` --version ${chart.version}` : ''}`;
}

function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); } catch { setCopied(false); }
  };
  return <button aria-label={label} className="publish-copy" onClick={copy} type="button">{copied ? 'Copied' : 'Copy'}</button>;
}
CopyButton.propTypes = { label: PropTypes.string.isRequired, text: PropTypes.string.isRequired };

/**
 * Git and GitOps, the confirm step: the account, a new or an existing repository, the branch (or a new branch with a
 * merge request), what goes along. `onSubmit(body)` with the body of POST /publish/git (or /publish/gitops).
 */
export function GitConfirm({ kind, targets, defaultName, services, initialForm, onBack, onSubmit }) {
  const accounts = targets?.git?.accounts || [];
  const first = initialForm || {};
  const [accountId, setAccountId] = useState(() => (accounts.some((a) => a.id === first.accountId) ? first.accountId : (accounts.find((a) => a.isDefault) || accounts[0])?.id || ''));
  const [mode, setMode] = useState(first.mode || 'new');
  const [name, setName] = useState(first.name ?? (kind === 'gitops' ? `${defaultName}-gitops` : defaultName));
  const [namespace, setNamespace] = useState(first.namespace || '');
  const [isPrivate, setIsPrivate] = useState(first.isPrivate ?? true);
  const [azureProject, setAzureProject] = useState(first.azureProject || '');
  const [repoUrl, setRepoUrl] = useState(first.repoUrl || '');
  const [branch, setBranch] = useState(first.branch || '');
  const [mergeRequest, setMergeRequest] = useState(Boolean(first.mergeRequest));
  const [newBranch] = useState(() => first.newBranch || publishBranchName());
  const [includeDeploy, setIncludeDeploy] = useState(first.includeDeploy ?? true);
  const [environments, setEnvironments] = useState(first.environments || DEFAULT_ENVIRONMENTS);
  const [envInput, setEnvInput] = useState('');
  const account = accounts.find((a) => a.id === accountId) || null;
  const gitops = kind === 'gitops';
  const id = (field) => `publish-${kind}-${field}`;

  const addEnvironment = () => {
    const env = envInput.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
    if (env && !environments.includes(env)) { setEnvironments([...environments, env]); }
    setEnvInput('');
  };

  const valid = Boolean(account) && (mode === 'new' ? Boolean(name.trim()) : /^https?:\/\/\S+$/.test(repoUrl.trim()))
    && (!gitops || environments.length > 0);

  const submit = () => {
    const body = {};
    if (mode === 'new') {
      body.create = { provider: account.type, name: name.trim(), private: isPrivate };
      if (namespace.trim()) { body.create.namespace = namespace.trim(); }
      if (account.host) { body.create.host = account.host; }
      if (account.type === 'azure-devops' && azureProject.trim()) { body.create.project = azureProject.trim(); }
      body.account = account.id;
    } else {
      body.repoUrl = repoUrl.trim();
      // The account chosen goes along only for its own host; otherwise the server picks the person's account for that host.
      if (!account.host || account.host === hostOf(body.repoUrl)) { body.account = account.id; }
      if (mergeRequest) { body.newBranch = newBranch; body.mergeRequest = true; }
    }
    if (branch.trim()) { body.branch = branch.trim(); }
    if (gitops) {
      body.environments = environments;
    } else {
      body.include = { deploy: includeDeploy };
    }
    onSubmit(body, { accountId, mode, name, namespace, isPrivate, azureProject, repoUrl, branch, mergeRequest, newBranch, includeDeploy, environments });
  };

  return (
    <>
      {accounts.length === 0 && (
        <div className="publish-banner publish-banner-bad" data-testid="publish-git-no-account">
          <span>Connect GitHub, GitLab, Azure DevOps or Bitbucket in Connections, then come back.</span>
          <a className="publish-btn" href={targets?.git?.connectUrl || CONNECTIONS_PATH} rel="noopener noreferrer" target="_blank">Open Connections</a>
        </div>
      )}
      {gitops && (
        <p className="publish-note">
          A repository with what a cluster needs to run the app: the Helm chart, one values file per environment, and the
          Argo CD and Flux manifests. Argo CD or Flux deploys from it. No source code goes there.
        </p>
      )}
      {accounts.length > 0 && (
        <div className="publish-field">
          <label htmlFor={id('account')}>Account</label>
          <select id={id('account')} onChange={(event) => setAccountId(event.target.value)} value={accountId}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.label || `${providerName(a.type)} · ${a.login || a.name}`}{a.host ? ` (${a.host})` : ''}</option>
            ))}
          </select>
        </div>
      )}
      <fieldset className="publish-question">
        <legend>Repository</legend>
        <div className="publish-choice-row">
          <label className={`publish-chip${mode === 'new' ? ' is-on' : ''}`}>
            <input checked={mode === 'new'} name={id('mode')} onChange={() => setMode('new')} type="radio" /> New repository
          </label>
          <label className={`publish-chip${mode === 'existing' ? ' is-on' : ''}`}>
            <input checked={mode === 'existing'} name={id('mode')} onChange={() => setMode('existing')} type="radio" /> Existing repository
          </label>
        </div>
        {mode === 'new' ? (
          <>
            <div className="publish-field">
              <label htmlFor={id('name')}>Repository name</label>
              <input id={id('name')} onChange={(event) => setName(event.target.value)} value={name} />
            </div>
            <div className="publish-field">
              <label htmlFor={id('namespace')}>Owner or group (optional)</label>
              <input id={id('namespace')} onChange={(event) => setNamespace(event.target.value)} placeholder={account?.login || ''} value={namespace} />
            </div>
            {account?.type === 'azure-devops' && (
              <div className="publish-field">
                <label htmlFor={id('project')}>Azure DevOps project</label>
                <input id={id('project')} onChange={(event) => setAzureProject(event.target.value)} value={azureProject} />
              </div>
            )}
            <label className="publish-check">
              <input checked={isPrivate} onChange={(event) => setIsPrivate(event.target.checked)} type="checkbox" /> Private repository
            </label>
          </>
        ) : (
          <div className="publish-field">
            <label htmlFor={id('url')}>Repository URL</label>
            <input id={id('url')} onChange={(event) => setRepoUrl(event.target.value)} placeholder="https://github.com/acme/fleet-tracker" value={repoUrl} />
          </div>
        )}
      </fieldset>
      <div className="publish-field">
        <label htmlFor={id('branch')}>Branch</label>
        <input id={id('branch')} onChange={(event) => setBranch(event.target.value)} placeholder={mode === 'new' ? 'main' : 'The repository\'s default branch'} value={branch} />
      </div>
      {mode === 'existing' && (
        <label className="publish-check">
          <input checked={mergeRequest} onChange={(event) => setMergeRequest(event.target.checked)} type="checkbox" />
          <span>
            Push to a new branch and open a merge request
            {mergeRequest && <small className="publish-check-hint"> {newBranch}, into {branch.trim() || 'the default branch'}</small>}
          </span>
        </label>
      )}
      {gitops ? (
        <div className="publish-field">
          <span className="publish-label">Environments</span>
          <div className="publish-services publish-envs">
            {environments.map((env) => (
              <span key={env}>
                {env}
                <button aria-label={`Remove ${env}`} className="publish-env-x" onClick={() => setEnvironments(environments.filter((e) => e !== env))} type="button">&times;</button>
              </span>
            ))}
            <input
              aria-label="Add an environment"
              className="publish-env-input"
              onBlur={addEnvironment}
              onChange={(event) => setEnvInput(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ',') { event.preventDefault(); addEnvironment(); } }}
              placeholder="Add…"
              value={envInput}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="publish-field">
            <span className="publish-label">Services included</span>
            <div className="publish-services">
              {(services?.length ? services : ['project']).map((service) => <span key={service}>{service}</span>)}
            </div>
          </div>
          <label className="publish-check">
            <input checked={includeDeploy} onChange={(event) => setIncludeDeploy(event.target.checked)} type="checkbox" /> Include deployment files (Helm chart and docker-compose)
          </label>
          <p className="publish-note">Chat history and brain files are not included.</p>
        </>
      )}
      <div className="publish-row">
        <button className="publish-btn" onClick={onBack} type="button">Back</button>
        <button className="publish-btn publish-btn-primary" disabled={!valid} onClick={submit} type="button">Publish</button>
      </div>
    </>
  );
}

GitConfirm.propTypes = {
  defaultName: PropTypes.string.isRequired,
  initialForm: PropTypes.object,
  kind: PropTypes.oneOf(['git', 'gitops']).isRequired,
  onBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  services: PropTypes.array,
  targets: PropTypes.object,
};

/** Git and GitOps, the result: the repository, the branch and commit, the merge request (or the compare link). */
export function GitResult({ kind, result, projectName, withDeploy, onClose }) {
  const [copied, setCopied] = useState(false);
  const url = result.webUrl || result.repoUrl;
  const mr = result.mergeRequest?.url || null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); } catch { setCopied(false); }
  };
  return (
    <>
      <div className="publish-ok"><i>✓</i>{projectName} is in {repoPath(url)}</div>
      <div className="publish-link">{url}</div>
      <p className="publish-note" data-testid="publish-git-detail">
        {result.created ? 'New repository. ' : ''}
        Branch {result.branch}{result.commit ? `, commit ${String(result.commit).slice(0, 7)}` : ''}
        {typeof result.filesPushed === 'number' ? `, ${result.filesPushed} files` : ''}.
        {result.account?.label ? ` Pushed as ${result.account.label}.` : ''}
      </p>
      {kind === 'gitops'
        ? <p className="publish-note">Point Argo CD or Flux at this repository: argocd/ and flux/ have what each needs.</p>
        : <p className="publish-note">Deploy it with your usual pipeline{withDeploy ? ', or with the Helm chart and the compose file in deploy/' : ''}.</p>}
      <div className="publish-row">
        <button className="publish-btn" onClick={copy} type="button">{copied ? 'Copied' : 'Copy link'}</button>
        {!mr && result.compareUrl && <a className="publish-btn" href={result.compareUrl} rel="noopener noreferrer" target="_blank">Compare changes</a>}
        <a className={`publish-btn${mr ? '' : ' publish-btn-primary'}`} href={url} onClick={mr ? undefined : onClose} rel="noopener noreferrer" target="_blank">Open repository</a>
        {mr && <a className="publish-btn publish-btn-primary" href={mr} onClick={onClose} rel="noopener noreferrer" target="_blank">Open merge request</a>}
      </div>
    </>
  );
}

GitResult.propTypes = {
  kind: PropTypes.oneOf(['git', 'gitops']).isRequired,
  onClose: PropTypes.func.isRequired,
  projectName: PropTypes.string,
  result: PropTypes.object.isRequired,
  withDeploy: PropTypes.bool,
};

const PLATFORMS = ['linux/amd64', 'linux/arm64'];

/**
 * Image registry, the confirm step: which services, which platforms, the tag, the chart. `onSubmit({ services,
 * platforms, tag, chart })` for POST /publish/images.
 */
export function RegistryConfirm({ projectId, registry, services, onBack, onSubmit }) {
  const [chosen, setChosen] = useState(services);
  const [platforms, setPlatforms] = useState(() => {
    const allowed = registry?.registry?.platforms;
    return Array.isArray(allowed) && allowed.length && !allowed.includes('linux/amd64') ? [allowed[0]] : ['linux/amd64'];
  });
  const [tag, setTag] = useState('');
  const [chart, setChart] = useState(true);
  const edited = useRef(false);
  const offered = registry?.registry?.platforms?.length ? PLATFORMS.filter((p) => registry.registry.platforms.includes(p)) : PLATFORMS;

  // The tag defaults to the version the deployment files carry.
  useEffect(() => {
    let live = true;
    getDeployFiles(projectId).then((files) => {
      if (live && !edited.current && files?.version) { setTag(tagFromVersion(files.version)); }
    }).catch(() => {});
    return () => { live = false; };
  }, [projectId]);

  const toggle = (list, value, set) => set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  const where = [registry?.registry?.host, registry?.registry?.prefix].filter(Boolean).join('/');
  const validTag = !tag || /^[A-Za-z0-9_][A-Za-z0-9_.-]{0,127}$/.test(tag);

  return (
    <>
      <p className="publish-note">
        Flow builds an image for each service{where ? ` and pushes it to ${where}` : ''}
        {registry?.registry?.signing ? ', signed, ' : ' '}with its SBOM and build provenance.
      </p>
      <div className="publish-field">
        <span className="publish-label">Services</span>
        <div className="publish-checks">
          {services.map((service) => (
            <label className="publish-check" key={service}>
              <input checked={chosen.includes(service)} onChange={() => toggle(chosen, service, setChosen)} type="checkbox" /> {service}
            </label>
          ))}
        </div>
      </div>
      <div className="publish-field">
        <span className="publish-label">Platforms</span>
        <div className="publish-checks">
          {offered.map((platform) => (
            <label className="publish-check" key={platform}>
              <input checked={platforms.includes(platform)} onChange={() => toggle(platforms, platform, setPlatforms)} type="checkbox" /> {platform}
            </label>
          ))}
        </div>
      </div>
      <div className="publish-field">
        <label htmlFor="publish-image-tag">Tag</label>
        <input id="publish-image-tag" onChange={(event) => { edited.current = true; setTag(event.target.value.trim()); }} placeholder="The project's version" value={tag} />
        {!validTag && <small className="publish-test-bad">Letters, digits, dots, dashes and underscores only.</small>}
      </div>
      <label className="publish-check">
        <input checked={chart} onChange={(event) => setChart(event.target.checked)} type="checkbox" /> Also publish the Helm chart
      </label>
      <div className="publish-row">
        <button className="publish-btn" onClick={onBack} type="button">Back</button>
        <button
          className="publish-btn publish-btn-primary"
          disabled={!chosen.length || !platforms.length || !validTag}
          onClick={() => onSubmit({ services: chosen, platforms, tag: tag || undefined, chart })}
          type="button"
        >
          Build and publish
        </button>
      </div>
    </>
  );
}

RegistryConfirm.propTypes = {
  onBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  registry: PropTypes.object,
  services: PropTypes.array.isRequired,
};

const STATE_LABELS = {
  queued: 'Waiting', building: 'Building', signing: 'Signing', 'pushing-chart': 'Publishing the chart', succeeded: 'Done', failed: 'Failed', pushing: 'Pushing',
};
export const stateLabel = (state) => STATE_LABELS[state] || state || 'Waiting';

/**
 * Image registry, after the start: the run every 2 s until it ends, each service's state; then each image with its
 * digest and the `helm install` command. `onDone(run)` once, when it ends.
 */
export function RegistryRun({ projectId, initialRun, onDone, onClose, onBack }) {
  const [run, setRun] = useState(initialRun);
  const [pollError, setPollError] = useState(null);
  const reported = useRef(false);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const done = RUN_DONE.includes(run?.state);

  useEffect(() => {
    if (done) {
      if (!reported.current) { reported.current = true; doneRef.current?.(run); }
      return undefined;
    }
    let live = true;
    const timer = setInterval(() => {
      getImageRun(projectId, run.id)
        .then((answer) => { if (live && answer?.run) { setRun(answer.run); setPollError(null); } })
        .catch((err) => { if (live) { setPollError(err?.message || 'Could not read the build.'); } });
    }, RUN_POLL_MS);
    return () => { live = false; clearInterval(timer); };
  }, [projectId, run, done]);

  const failed = run?.state === 'failed';
  const command = helmInstall(run?.chart);
  return (
    <>
      {!done && <p className="publish-note" aria-live="polite" role="status"><span aria-hidden className="publish-spin" /> {stateLabel(run?.state)}… It takes a few minutes; you can close this window, the History tab shows it when it is done.</p>}
      {run?.state === 'succeeded' && <div className="publish-ok"><i>✓</i>Images published</div>}
      {failed && <div className="publish-banner publish-banner-bad" role="alert"><span>The build failed{run.error ? `: ${run.error}` : '.'}</span></div>}
      {pollError && <div className="publish-banner publish-banner-warn"><span>{pollError}</span></div>}
      <ul className="publish-images" data-testid="publish-image-run">
        {(run?.services || []).map((service) => (
          <li key={service.name}>
            <div className="publish-image-head">
              <b>{service.name}</b>
              <span className={`publish-tag${service.state === 'succeeded' ? ' publish-tag-ok' : ''}${service.state === 'failed' ? ' publish-tag-bad' : ''}`}>{stateLabel(service.state)}</span>
            </div>
            {service.image && <code className="publish-image-name">{service.image}</code>}
            {service.digest && (
              <span className="publish-digest">
                <code title={service.digest}>{service.digest.startsWith('sha256:') ? 'sha256:' : ''}{shortDigest(service.digest)}</code>
                <CopyButton label={`Copy the digest of ${service.name}`} text={service.image ? `${service.image.replace(/:[^/:]+$/, '')}@${service.digest}` : service.digest} />
              </span>
            )}
            {service.error && <small className="publish-test-bad">{service.error}</small>}
          </li>
        ))}
      </ul>
      {run?.chart?.ref && (
        <div className="publish-field">
          <span className="publish-label">Helm chart {run.chart.version}</span>
          <div className="publish-command">
            <code>{command}</code>
            <CopyButton label="Copy the helm install command" text={command} />
          </div>
        </div>
      )}
      <div className="publish-row">
        {failed && <button className="publish-btn" onClick={onBack} type="button">Back</button>}
        <button className={`publish-btn${done ? ' publish-btn-primary' : ''}`} onClick={onClose} type="button">{done ? 'Done' : 'Close'}</button>
      </div>
    </>
  );
}

RegistryRun.propTypes = {
  initialRun: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func,
  projectId: PropTypes.string.isRequired,
};

/** The generated deployment files (GET /publish/deploy), read only: the chart's files and the compose file. */
export function DeployFilesPreview({ projectId, onBack }) {
  const [files, setFiles] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let live = true;
    getDeployFiles(projectId).then((answer) => {
      if (!live) { return; }
      const chartName = answer?.chart?.name || answer?.name || 'chart';
      const list = [
        ...(answer?.compose?.content ? [{ path: `deploy/${answer.compose.path || 'docker-compose.prod.yml'}`, content: answer.compose.content }] : []),
        ...(answer?.chart?.files || []).map((file) => ({ path: `deploy/helm/${chartName}/${file.path}`, content: file.content })),
      ];
      setFiles({ list, notes: answer?.notes || [], version: answer?.version });
      setSelected(list[0]?.path || null);
    }).catch((err) => { if (live) { setError(err?.message || 'The deployment files could not be read.'); } });
    return () => { live = false; };
  }, [projectId]);

  const current = files?.list.find((file) => file.path === selected);
  return (
    <>
      {error && <div className="publish-banner publish-banner-bad"><span>{error}</span></div>}
      {!files && !error && <p className="publish-note"><span aria-hidden className="publish-spin" /> Reading the deployment files…</p>}
      {files && (
        <>
          <p className="publish-note">What Git, the registry and the zip get, generated from the project{files.version ? ` (version ${files.version})` : ''}. Read only.</p>
          {files.notes.map((note) => <p className="publish-note" key={note}>{note}</p>)}
          <div className="publish-files">
            <ul aria-label="Deployment files" className="publish-file-list">
              {files.list.map((file) => (
                <li key={file.path}>
                  <button aria-pressed={file.path === selected} onClick={() => setSelected(file.path)} type="button">{file.path}</button>
                </li>
              ))}
            </ul>
            {current && <pre aria-label={current.path} className="publish-file-code" tabIndex={0}>{current.content}</pre>}
          </div>
        </>
      )}
      <div className="publish-row">
        <button className="publish-btn" onClick={onBack} type="button">Back</button>
      </div>
    </>
  );
}

DeployFilesPreview.propTypes = { onBack: PropTypes.func.isRequired, projectId: PropTypes.string.isRequired };
